/**
 * SiteNav — THE canonical HiBFF menu, identical on every public page and in
 * every language. Tabs: Home · How It Works · Why HiBFF · Safety · Pricing ·
 * About · Schools · Newsletter, plus LanguageSwitcher, Log In, Get Started.
 * `current` highlights the active tab; `mode` = 'fixed' | 'sticky'.
 * Includes its own mobile hamburger drawer.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const PURPLE = '#7B4FD8';
const NAV_OFFSET = 140;

export const SiteNav = ({ current = '', mode = 'sticky' }) => {
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  const [open, setOpen] = useState(false);

  const links = [
    { id: 'home', label: t('nav.home'), href: '/' },
    { id: 'how', label: t('nav.how'), href: '/how-it-works', section: 'how-it-works' },
    { id: 'why', label: t('nav.why'), href: '/why-hibff', section: 'founder-story' },
    { id: 'safety', label: t('nav.safety'), href: '/safety', section: 'safety' },
    { id: 'pricing', label: t('nav.pricing'), href: '/pricing', section: 'pricing' },
    { id: 'about', label: t('nav.about'), href: '/about' },
    { id: 'schools', label: t('nav.schools'), href: '/education' },
    { id: 'family', label: t('nav.family'), href: '/teens-parents', section: 'how-it-works' },
    { id: 'adults', label: t('nav.adults'), href: '/adults', section: 'adults' },
  ];

  // Section tabs: smooth-scroll when the section is on the current page and
  // rewrite the address bar to the clean URL (no # fragment). From any other
  // page, navigate to the clean route which scrolls on load.
  const onLinkClick = (e, l) => {
    setOpen(false);
    if (!l.section) return;
    e.preventDefault();
    const el = document.getElementById(l.section);
    if (el) {
      window.history.pushState({}, '', l.href);
      const top = el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      navigate(l.href);
    }
  };

  const positionCls = mode === 'fixed'
    ? 'fixed top-0 inset-x-0 z-40'
    : 'sticky top-0 z-40';

  return (
    <nav dir={dir} className={`${positionCls} bg-white/90 backdrop-blur-sm border-b border-gray-100`} style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }} data-testid="site-nav">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
        <a href="/" className="flex-shrink-0" data-testid="site-nav-logo">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-16 sm:h-20 lg:h-24 w-auto" />
        </a>
        <div className="hidden md:flex items-center gap-5 lg:gap-6">
          {links.map(l => (
            <a
              key={l.id}
              href={l.href}
              onClick={(e) => onLinkClick(e, l)}
              className={`text-sm font-medium transition-colors hover:text-[#7B4FD8] whitespace-nowrap ${current === l.id ? 'font-semibold' : ''}`}
              style={{ color: current === l.id ? PURPLE : '#555' }}
              data-testid={`site-nav-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <LanguageSwitcher className="hidden sm:flex" />
          <button onClick={() => navigate('/login')} className="hidden sm:block px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 whitespace-nowrap" data-testid="site-nav-login">
            {t('nav.login')}
          </button>
          <button onClick={() => navigate('/register')} className="hidden sm:block px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white rounded-lg whitespace-nowrap" style={{ backgroundColor: PURPLE }} data-testid="site-nav-register">
            {t('nav.getStarted')}
          </button>
          <button onClick={() => setOpen(!open)} className="md:hidden p-2.5 min-w-[40px] min-h-[40px] rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="site-nav-mobile-btn" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — compact, top-right */}
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="md:hidden fixed inset-0 z-[65] bg-black/40"
            data-testid="site-nav-mobile-backdrop"
          />
          <div
            dir={dir}
            className={`md:hidden fixed top-3 z-[70] w-[60%] max-w-[240px] max-h-[calc(100vh-24px)] bg-white shadow-2xl border border-gray-100 rounded-xl flex flex-col overflow-hidden ${dir === 'rtl' ? 'left-3' : 'right-3'}`}
            data-testid="site-nav-mobile-drawer"
          >
            <div className={`flex px-2 pt-2 ${dir === 'rtl' ? 'justify-start' : 'justify-end'}`}>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close" data-testid="site-nav-mobile-close">
                <X size={20} />
              </button>
            </div>
            <div className="px-3 pb-2 space-y-0.5 overflow-y-auto">
              {links.map(l => (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={(e) => onLinkClick(e, l)}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:text-[#7B4FD8] ${current === l.id ? 'text-[#7B4FD8] font-semibold' : 'text-gray-700'}`}
                  data-testid={`site-nav-mobile-${l.id}`}
                >
                  {l.label}
                </a>
              ))}
              <div className="px-3 py-2">
                <LanguageSwitcher />
              </div>
            </div>
            <div className="border-t border-gray-100 px-3 py-3 flex flex-col gap-2 bg-white">
              <button onClick={() => { setOpen(false); navigate('/login'); }} data-testid="site-nav-mobile-login" className="w-full py-2 text-sm font-semibold text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50">{t('nav.login')}</button>
              <button onClick={() => { setOpen(false); navigate('/register'); }} data-testid="site-nav-mobile-register" className="w-full py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90" style={{ backgroundColor: PURPLE }}>{t('nav.getStarted')}</button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default SiteNav;
